import { HdDocRef } from './hd-doc-ref.model';
import { HD_CIRCUIT_INFO_DEFAULT_VARIANT, HdCircuitInfoVariant } from '../circuit-info/hd-circuit-info-variant';
import { findHdBodygraphChannelByGates, getHdBodygraphChannel } from '../hd-bodygraph-metadata';
import { getHdBodygraphCircuit } from '../hd-bodygraph-circuit-metadata';
import { getHdBodygraphStream } from '../hd-bodygraph-stream-metadata';

/** Trailing `_compact` / `_normal` / `_extended` op een circuit-bestandsnaam. */
const CIRCUIT_VARIANT_SUFFIX = /_(compact|normal|extended)$/;

/**
 * Vertaalt een markdown-link (zoals hij letterlijk in een `.md`-bestand staat)
 * naar een {@link HdDocRef}, of `null` wanneer de link niet naar een intern
 * doc-bestand verwijst (externe link, in-page anker, niet-markdown, of een
 * onbekende naam) — in dat geval laat de interceptor het standaard browser-
 * gedrag (nieuw tabblad) staan.
 *
 * **Pad-agnostisch**: enkel de *bestandsnaam* telt. De doc-bronnen linken
 * onderling met inconsistente relatieve paden (`../streams/…`, `../circuits/…`,
 * platte namen, soms zónder variant-suffix) die niet matchen met de afgevlakte
 * asset-layout. Door op de basename te classificeren hoeven de auteursbestanden
 * niet aangepast te worden.
 *
 * Geef bij voorkeur de **rauwe** `href` (het `getAttribute('href')`, dus
 * relatief zoals geschreven) door — niet de ge-resolvede absolute `.href` —
 * zodat origin-verschillen geen rol spelen.
 */
export function resolveDocLink(href: string | null | undefined): HdDocRef | null {
  if (href == null) return null;
  const trimmed = href.trim();
  if (!trimmed) return null;

  // Pure in-page anker → geen doc-navigatie.
  if (trimmed.startsWith('#')) return null;

  // Niet-http(s) protocollen (mailto:, tel:, …) → extern.
  const protocolMatch = trimmed.match(/^([a-z][a-z0-9+.-]*):/i);
  if (protocolMatch && !/^https?$/i.test(protocolMatch[1])) return null;

  const basename = extractDocBasename(trimmed);
  if (!basename) return null;

  // Algemeen overzicht (README / README_extended).
  if (/^readme/i.test(basename)) {
    return { kind: 'overview' };
  }

  // Circuit — met optionele variant-suffix; onbekende suffix valt terug op default.
  const variantMatch = basename.match(CIRCUIT_VARIANT_SUFFIX);
  const circuitCandidate = basename.replace(CIRCUIT_VARIANT_SUFFIX, '');
  if (getHdBodygraphCircuit(circuitCandidate)) {
    const variant: HdCircuitInfoVariant =
      (variantMatch?.[1] as HdCircuitInfoVariant | undefined) ?? HD_CIRCUIT_INFO_DEFAULT_VARIANT;
    return { kind: 'circuit', circuitKey: circuitCandidate, variant };
  }

  // Bewustzijnsstroom (geen variant).
  if (getHdBodygraphStream(basename)) {
    return { kind: 'stream', streamKey: basename };
  }

  const channelKey = resolveChannelKey(basename);
  if (channelKey !== null) {
    return { kind: 'channel', channelKey };
  }

  return null;
}

/**
 * Haalt de doc-basename (bestandsnaam zonder `.md`) uit een href. Strippt
 * eerst query + hash. Retourneert `null` wanneer het doel geen `.md`-bestand is.
 */
function extractDocBasename(href: string): string | null {
  const path = href.split(/[?#]/, 1)[0];
  if (!path || !/\.md$/i.test(path)) return null;
  const segments = path.split('/');
  const last = segments[segments.length - 1];
  const name = last.replace(/\.md$/i, '');
  return name || null;
}

function resolveChannelKey(basename: string): string | null {
  if (getHdBodygraphChannel(basename)) {
    return basename;
  }

  const gatePairMatch = basename.match(/^(\d{1,2})-(\d{1,2})$/);
  if (!gatePairMatch) {
    return null;
  }

  const gateA = Number(gatePairMatch[1]);
  const gateB = Number(gatePairMatch[2]);
  return findHdBodygraphChannelByGates(gateA, gateB)?.key ?? null;
}
